import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/auth-store'
import { useAppStore } from '@/lib/store'
import { WelcomeModal } from './WelcomeModal'
import { RiskProfileModal } from '@/components/goals/RiskProfileModal'
import type { RiskProfile } from '@/lib/types'

type OnboardingStep = 'welcome' | 'risk-profile' | 'complete'

export function OnboardingFlow() {
  const { isAuthenticated, hasCompletedOnboarding, setOnboardingComplete } = useAuthStore()
  const { riskProfile, setRiskProfile } = useAppStore()
  const [step, setStep] = useState<OnboardingStep>('welcome')
  const [currentBalance, setCurrentBalance] = useState(1000) // Default balance for calculation

  // Fetch current balance when component mounts
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem('sigma_token')
        if (!token) return

        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'https://api.futurabroker.com/api/v2'}/wallet/balance`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.data?.real_balance) {
            setCurrentBalance(data.data.real_balance)
          }
        }
      } catch (error) {
        console.error('[Onboarding] Error fetching balance:', error)
      }
    }

    if (isAuthenticated && !hasCompletedOnboarding) {
      fetchBalance()
    }
  }, [isAuthenticated, hasCompletedOnboarding])

  // Don't render if not authenticated or already completed onboarding
  if (!isAuthenticated || hasCompletedOnboarding) {
    return null
  }

  const handleWelcomeContinue = () => {
    setStep('risk-profile')
  }

  const handleSelectProfile = (profile: RiskProfile) => {
    setRiskProfile(profile)
    setOnboardingComplete()
    setStep('complete')
  }

  // If complete, don't render anything
  if (step === 'complete') {
    return null
  }

  return (
    <>
      {/* Step 1: Welcome Modal */}
      <WelcomeModal open={step === 'welcome'} onContinue={handleWelcomeContinue} />

      {/* Step 2: Risk Profile Modal */}
      <RiskProfileModal
        open={step === 'risk-profile'}
        onOpenChange={(open) => {
          // Don't allow closing without selecting a profile
          if (!open && step === 'risk-profile') {
            return
          }
        }}
        selectedProfile={riskProfile}
        onSelectProfile={handleSelectProfile}
        currentBalance={currentBalance}
      />
    </>
  )
}
